import type { ComponentType, ReactNode } from 'react';

import BlankLayout from '@/layouts/blank';
import CMSLayout from '@/layouts/cms';
import PublicLayout from '@/layouts/public';
import { LayoutType } from '@/types/shared';

export type PageComponent = ComponentType & {
  layout?: (page: ReactNode) => ReactNode | null;
  layoutType?: LayoutType;
};

export type ResolvedComponent = {
  default: PageComponent;
};

// Lazy load pages for code splitting - each page is loaded on demand
const pages = import.meta.glob([
  '../pages/**/*.tsx',
  '!../pages/**/_components/**',
  '!../pages/**/*.test.tsx',
]) as Record<string, () => Promise<ResolvedComponent>>;

export async function resolvePage(name: string): Promise<ResolvedComponent> {
  const pagePath = `../pages/${name}.tsx`;
  const pageLoader = pages[pagePath];

  if (!pageLoader) {
    const message = `Missing Inertia page component: '${name}.tsx'`;
    if (import.meta.env.DEV) {
      console.error(message);
    }
    const notFoundLoader = pages['../pages/public/404.tsx'];
    if (notFoundLoader) {
      const notFoundPage = await notFoundLoader();
      notFoundPage.default.layoutType ||= LayoutType.PUBLIC;
      return notFoundPage;
    }
    throw new Error(message);
  }

  const page = await pageLoader();

  if (typeof page.default?.layout === 'function') {
    return page;
  }

  const layoutType = page.default?.layoutType;

  page.default.layout = (pageContent: ReactNode) => {
    const layouts: Record<string, ComponentType<{ children: ReactNode }>> = {
      [LayoutType.PUBLIC]: PublicLayout,
      [LayoutType.CMS]: CMSLayout,
      [LayoutType.BLANK]: BlankLayout,
    };

    if (layoutType && layouts[layoutType as LayoutType]) {
      const LayoutComponent = layouts[layoutType as LayoutType];
      return <LayoutComponent>{pageContent as ReactNode}</LayoutComponent>;
    }

    const LayoutComponent =
      Object.entries(layouts).find(([key]) => name.startsWith(key))?.[1] || BlankLayout;

    return <LayoutComponent>{pageContent as ReactNode}</LayoutComponent>;
  };

  return page;
}
